import * as React from "react";
import { DevButton, DevPanel, devPanelColors as c } from "@base/lib/dev-toolbar/DevPanel";
import { useQuizActions, useQuizSnapshot } from "../internal/state";

/**
 * The dev toolbar's "quiz slides" tool: where you are, the current slide's
 * answer state, and every registered slide (click to jump). Jumps skip
 * validation, same as the quiz's own goTo* actions.
 */
export function QuizSlidesDebugPanel() {
  const snap = useQuizSnapshot();
  const actions = useQuizActions();
  const currentRowRef = React.useRef<HTMLButtonElement>(null);

  const idx = snap.currentIdx;
  const total = snap.slides.length;
  const slide = snap.currentSlide;
  const state = snap.currentSlideState;

  // Keep the current slide visible in the list as you move through the quiz.
  React.useEffect(() => {
    currentRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [snap.currentSlideID]);

  return (
    <DevPanel
      toolID="quizSlides"
      title="Quiz slides"
      side="left"
      actions={
        <>
          <DevButton disabled={idx <= 0} onClick={() => actions.goToPrev()}>
            ‹ prev
          </DevButton>
          <DevButton disabled={idx < 0 || idx >= total - 1} onClick={() => actions.goToNext()}>
            next ›
          </DevButton>
        </>
      }
    >
      {slide && state ? (
        <table style={{ borderCollapse: "collapse" }}>
          <tbody>
            <Row label="slide">
              {idx + 1} / {total}
            </Row>
            <Row label="id">
              <strong>{slide.id}</strong>
            </Row>
            <Row label="type">
              {slide.type}
              {slide.optional ? " (optional)" : ""}
            </Row>
            <Row label="answer">
              {"value" in state && state.value !== undefined ? json(state.value) : "—"}
            </Row>
            <Row label="valid">
              <span style={{ color: state.isValid ? c.on : c.warn }}>
                {state.isValid ? "yes" : "no"}
              </span>
              {` · confirmed: ${state.confirmed ? "yes" : "no"} · attempts: ${state.attempts}`}
            </Row>
          </tbody>
        </table>
      ) : (
        <div style={{ color: c.faint }}>No slide yet ({total} registered).</div>
      )}

      <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px solid ${c.border}`, color: c.muted }}>
        All slides (click to jump) · ✓ = confirmed
      </div>
      <div style={{ maxHeight: 180, overflowY: "auto" }}>
        {snap.slides.map((s, i) => {
          const isCurrent = s.id === snap.currentSlideID;
          const confirmed = snap.slideStateByID[s.id]?.confirmed;
          return (
            <button
              key={s.id}
              ref={isCurrent ? currentRowRef : undefined}
              onClick={() => actions.goToSlideIdx(i)}
              style={{
                display: "flex",
                gap: 8,
                width: "100%",
                padding: "1px 4px",
                border: "none",
                borderRadius: 3,
                background: isCurrent ? c.border : "transparent",
                color: isCurrent ? c.text : c.muted,
                font: "inherit",
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <span style={{ width: 20, textAlign: "right", color: c.faint }}>{i + 1}</span>
              <span style={{ flex: 1, wordBreak: "break-all" }}>{s.id}</span>
              <span style={{ color: c.faint }}>{s.type}</span>
              <span style={{ width: 10, color: c.on }}>{confirmed ? "✓" : ""}</span>
            </button>
          );
        })}
      </div>
    </DevPanel>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr style={{ verticalAlign: "top" }}>
      <td style={{ padding: "1px 10px 1px 0", color: c.muted, whiteSpace: "nowrap" }}>{label}</td>
      <td style={{ padding: "1px 0", wordBreak: "break-word" }}>{children}</td>
    </tr>
  );
}

function json(value: unknown): string {
  try {
    const text = JSON.stringify(value);
    return text.length > 160 ? `${text.slice(0, 159)}…` : text;
  } catch {
    return String(value);
  }
}
